import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, List, Map as MapIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, errMsg } from "@/lib/api";
import { useMe, can } from "@/context/MeContext";
import { PageHeader } from "@/components/PageHeader";
import { SpecialtyTree } from "@/components/SpecialtyTree";
import { DirectoryToolbar } from "@/components/directory/DirectoryToolbar";
import { DirectoryList } from "@/components/directory/DirectoryList";
import { DirectoryMap } from "@/components/directory/DirectoryMap";
import { DirectoryForm } from "@/components/directory/DirectoryForm";

const qs = (o) => new URLSearchParams(Object.entries(o).filter(([, v]) => v !== "" && v != null)).toString();

export default function DirectoryPage() {
  const api = useApi();
  const me = useMe();
  const writer = can(me, "write:directory");
  const [meta, setMeta] = useState(null);
  const [sel, setSel] = useState({});
  const [filters, setFilters] = useState({ q: "", wilaya: "", verified: "" });
  const [page, setPage] = useState(1);
  const [view, setView] = useState("list");
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const params = { category: sel.cat, subcategory: sel.sub, ...filters };
  const key = qs(params);

  const loadMeta = useCallback(() => api("get", "/directory/meta").then(setMeta).catch((e) => toast.error(errMsg(e))), [api]);
  const load = useCallback(() => {
    const path = view === "map" ? `/directory/map?${key}` : `/directory?${key}&page=${page}&limit=50`;
    return api("get", path).then(setData).catch((e) => toast.error(errMsg(e)));
  }, [api, key, page, view]);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { setPage(1); }, [key]);
  useEffect(() => { setData(null); const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const saved = () => { setEdit(null); load(); loadMeta(); };

  return (
    <div className="space-y-8" data-testid="directory-page">
      <PageHeader title="Annuaire Sehatic-Pedia" subtitle="Tous les professionnels et établissements de santé de l'annuaire de l'application, synchronisés en permanence.">
        {writer && <Button className="bg-[#C2410C] hover:bg-[#9A3412] text-white" onClick={() => setEdit({})} data-testid="directory-add-button"><Plus className="h-4 w-4 mr-1.5" /> Ajouter un praticien</Button>}
      </PageHeader>
      {meta && (
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-stone-600" data-testid="directory-totals">
          <span><strong className="text-stone-900 text-lg font-heading">{meta.totals.total.toLocaleString("fr-FR")}</strong> fiches</span>
          <span><strong className="text-stone-900 text-lg font-heading">{meta.totals.verified.toLocaleString("fr-FR")}</strong> vérifiées</span>
          <span><strong className="text-stone-900 text-lg font-heading">{meta.totals.added_here}</strong> ajoutées depuis la gestion</span>
        </div>
      )}
      <div className="grid lg:grid-cols-[300px_1fr] gap-6 items-start">
        <SpecialtyTree tree={meta?.tree || []} sel={sel} onSelect={setSel} />
        <div className="space-y-4 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <DirectoryToolbar filters={filters} setFilters={setFilters} wilayas={meta?.wilayas || []} />
            <div className="ml-auto flex rounded-md border border-stone-200 bg-white overflow-hidden">
              {[["list", List, "Liste"], ["map", MapIcon, "Carte"]].map(([v, Icon, label]) => (
                <button key={v} type="button" onClick={() => setView(v)} data-testid={`directory-view-${v}`}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm transition-colors duration-150 ${view === v ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`}><Icon className="h-4 w-4" />{label}</button>
              ))}
            </div>
          </div>
          <p className="text-sm text-stone-600" data-testid="directory-selection">{sel.sub ? `${sel.cat} › ${sel.sub}` : sel.cat || "Toutes les catégories"}{data ? ` — ${data.total.toLocaleString("fr-FR")} résultat(s)` : ""}</p>
          {view === "map"
            ? <DirectoryMap data={data} onOpen={(p) => api("get", `/sp/directory/${p.id}`).then(setEdit).catch((e) => toast.error(errMsg(e)))} />
            : <DirectoryList data={data} page={page} setPage={setPage} onOpen={setEdit} />}
        </div>
      </div>
      <DirectoryForm item={edit} meta={meta} writer={writer} deleter={can(me, "delete")} onClose={() => setEdit(null)} onSaved={saved} />
    </div>
  );
}
